import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  RefreshOutlined,
  ShieldOutlined,
  ScheduleOutlined,
  TrendingDownOutlined,
  TuneOutlined,
  ArrowForwardOutlined,
  ArrowDownwardOutlined,
  CampaignOutlined,
  InfoOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { ResponsiveLine } from '@nivo/line';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { FilterContext } from '../../contexts/FilterContext';
import Header from '../../components/Header';
import { api } from '../../helpers/apiUtils';
import { tokens } from '../../theme';
import {
  formatBerlin,
  renderEtkp,
  renderEur,
  renderInt,
  renderRate,
} from '../sam-scheduling/shared';

type Cohort = 'inactive' | 'engaged_non_converter';

interface CohortSummary {
  cohort: Cohort;
  total: number;
  mc_synced: number;
  mc_pending: number;
  mc_failed: number;
  iterable_synced: number;
  iterable_pending: number;
  source_rolling: number;
  source_backfill: number;
  source_initial: number;
  last_added_at: string | null;
}

interface ListGrowthPoint {
  day: string;
  migrated_inactive_cum: number;
  migrated_engaged_cum: number;
  dnv_subscribed: number | null;
  dnv_inactive: number | null;
  dnv_active: number | null;
}

interface InaktivState {
  date: string;
  subscribed: number;
  inactive: number;
  active: number;
  unsubscribed: number;
  cleaned: number;
  inactive_delta: number | null;
  subscribed_delta: number | null;
  active_delta: number | null;
}

type TierBucket = 'recent' | 'stale' | 'dark';

interface TierStat {
  total: number;
  probing: number;
  reactivated: number;
  permanent_dark: number;
}

type TierMap = Record<TierBucket, TierStat>;

type MailingType = 'sam_dnv' | 'reactivation' | 'asset_analysis' | 'plx' | 'webinar' | 'other';

type EvalClient = 'dnv' | 'kme';
/** UI selector value: a single client or both (combined). */
type EvalClientFilter = EvalClient | 'both';

const EVAL_CLIENT_LABEL: Record<EvalClient, string> = { dnv: 'DNV', kme: 'KME' };
const EVAL_CLIENT_COLOR: Record<EvalClient, string> = { dnv: '#c62828', kme: '#00838f' };

interface MailingEvalRow {
  mc_campaign_id: string;
  client: EvalClient;
  mailing_type: MailingType;
  send_date: string;
  title: string | null;
  subject: string | null;
  sam_id: number | null;
  sam_status: string | null;
  product_name: string | null;
  sam_subject: string | null;
  asset_name: string | null;
  asset_isin: string | null;
  recipients: number | null;
  unique_opens: number | null;
  unique_clicks: number | null;
  unsubscribed: number | null;
  leads: number | null;
  revenue: number | null;
}

interface MailingDaily {
  day: string;
  sends: number;
  recipients: number;
  opens: number;
  clicks: number;
  leads: number;
  revenue: number;
}

interface MailingTypeBreakdown {
  type: MailingType;
  sends: number;
  recipients: number;
  opens: number;
  clicks: number;
  leads: number;
  revenue: number;
  etkp: number | null;
}

interface MailingEvalSummary {
  windowDays: number;
  totalSends: number;
  totalRecipients: number;
  totalOpens: number;
  totalClicks: number;
  totalLeads: number;
  totalRevenue: number;
  openRate: number | null;
  clickRate: number | null;
  leadRate: number | null;
  revenuePerLead: number | null;
  etkp: number | null;
}

interface MailingEvalData {
  rows: MailingEvalRow[];
  dailyAggregates: MailingDaily[];
  typeBreakdown: MailingTypeBreakdown[];
  summary: MailingEvalSummary;
}

const MAILING_TYPE_LABEL: Record<MailingType, string> = {
  sam_dnv: 'SAM',
  reactivation: 'Reaktivierung',
  asset_analysis: 'Asset-Analysis',
  plx: 'PLX',
  webinar: 'Webinar',
  other: 'Other',
};

const MAILING_TYPE_COLOR: Record<MailingType, string> = {
  sam_dnv: '#c62828',
  reactivation: '#6a1b9a',
  asset_analysis: '#1565c0',
  plx: '#2e7d32',
  webinar: '#ed6c02',
  other: '#757575',
};

interface TierRule {
  label: string;
  criterion: string;
  cadence: string;
  color: string;
}

const TIER_RULES: Record<TierBucket, TierRule> = {
  recent: {
    label: 'Recent',
    criterion: '≤ 180 Tage seit letztem Engagement',
    cadence: 'Probe-Mail alle 21 Tage · max. 3 Versuche',
    color: '#2e7d32',
  },
  stale: {
    label: 'Stale',
    criterion: '181 – 365 Tage Engagement-Stille',
    cadence: 'Probe-Mail alle 30 Tage · max. 3 Versuche',
    color: '#ed6c02',
  },
  dark: {
    label: 'Dark',
    criterion: '> 365 Tage oder nie engagiert',
    cadence: 'Einmal-Probe · max. 1 Versuch',
    color: '#424242',
  },
};

const COHORT_LABEL: Record<Cohort, string> = {
  inactive: 'Inactive (Cohort A)',
  engaged_non_converter: 'Engaged Non-Converter (Cohort B)',
};

const COHORT_BLURB: Record<Cohort, string[]> = {
  inactive: [
    'Hat auf FT seit Monaten keine Mail mehr geöffnet',
    'Auf FT komplett stillgelegt',
    'Auf DNV sofort als Inaktiv markiert',
  ],
  engaged_non_converter: [
    'Öffnet FT-Mails, kauft aber nicht',
    'Bleibt auf FT-Newsletter aktiv',
    'Auf DNV: 60 Tage Karenz, dann ggf. Inaktiv',
  ],
};

const COHORT_TARGET: Record<Cohort, number> = {
  // Initial classifier counts measured 2026-06-09 — used for backfill-progress
  // percentage display. Adjust if the Iterable user base shifts significantly.
  inactive: 77206,
  engaged_non_converter: 34246,
};

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString('de-DE');
const fmtSigned = (n: number | null | undefined) => {
  if (n == null) return '–';
  const v = Number(n);
  if (v === 0) return '±0';
  return (v > 0 ? '+' : '') + v.toLocaleString('de-DE');
};

// ── Scanner-click guard ────────────────────────────────────────────────────
// Security appliances (Proofpoint / Mimecast / Barracuda …) auto-click every
// link in an email to scan it. Those clicks land in Mailchimp's totals and
// occasionally survive even its `unique_subscriber_clicks` filter, inflating a
// single send's click rate 5–10×. The tell is an implausible click-to-open
// ratio: real readers of these SAMs click at ~3–7 % CTOR; a scanner storm pushes
// one send past 40 % (e.g. 2026-07-04: 1093 clicks / 2518 opens = 43 %). We flag
// such sends and drop them from the per-day click-rate series so one storm can't
// blow out the chart y-axis and hide the real ~0.2–0.4 % trend.
const SCANNER_CTOR_THRESHOLD = 0.25; // clicks/opens above this ⇒ scanner-suspect
const SCANNER_MIN_OPENS = 50; // ignore tiny sends — can't move a pooled rate

function isScannerSuspect(r: {
  unique_opens: number | null;
  unique_clicks: number | null;
}): boolean {
  const opens = r.unique_opens ?? 0;
  const clicks = r.unique_clicks ?? 0;
  return opens >= SCANNER_MIN_OPENS && clicks / opens > SCANNER_CTOR_THRESHOLD;
}

type MailingEvalRowFlagged = MailingEvalRow & { scannerSuspect: boolean };

interface MailingDailyGuarded extends MailingDaily {
  clicksClean: number; // clicks excl. scanner-suspect sends
  recipientsClean: number; // recipients of non-scanner sends (click-rate denominator)
  scannerSends: number; // # scanner-suspect sends that day
}

interface DerivedMailingEval {
  summary: MailingEvalSummary;
  typeBreakdown: MailingTypeBreakdown[];
  dailyAggregates: MailingDailyGuarded[];
  rows: MailingEvalRowFlagged[];
  scannerCount: number;
}

// Re-aggregates the per-campaign rows the API returns, applying the reactivation
// filter. Mirrors the backend /mailing-evaluation math exactly so KPI cards /
// breakdown / charts stay consistent — but done client-side so the toggles are
// instant (no refetch) and the scanner guard gets the per-campaign granularity
// the pre-aggregated daily series can't provide.
function deriveMailingEval(
  allRows: MailingEvalRow[],
  showReactivation: boolean,
): DerivedMailingEval {
  const rows: MailingEvalRowFlagged[] = (
    showReactivation ? allRows : allRows.filter((r) => r.mailing_type !== 'reactivation')
  ).map((r) => ({ ...r, scannerSuspect: isScannerSuspect(r) }));

  const sumIf = (
    rs: MailingEvalRowFlagged[],
    pick: (r: MailingEvalRowFlagged) => number | null | undefined,
  ): number => rs.reduce((acc, r) => acc + (Number(pick(r)) || 0), 0);

  // Daily aggregates (+ scanner-clean click numerator/denominator)
  const aggByDay = new Map<string, MailingDailyGuarded>();
  for (const r of rows) {
    const day = r.send_date.slice(0, 10);
    const agg =
      aggByDay.get(day) ??
      ({
        day,
        sends: 0,
        recipients: 0,
        opens: 0,
        clicks: 0,
        leads: 0,
        revenue: 0,
        clicksClean: 0,
        recipientsClean: 0,
        scannerSends: 0,
      } as MailingDailyGuarded);
    agg.sends += 1;
    agg.recipients += r.recipients ?? 0;
    agg.opens += r.unique_opens ?? 0;
    agg.clicks += r.unique_clicks ?? 0;
    agg.leads += r.leads ?? 0;
    agg.revenue += r.revenue ?? 0;
    if (r.scannerSuspect) {
      agg.scannerSends += 1;
    } else {
      agg.clicksClean += r.unique_clicks ?? 0;
      agg.recipientsClean += r.recipients ?? 0;
    }
    aggByDay.set(day, agg);
  }
  const dailyAggregates = Array.from(aggByDay.values()).sort((a, b) => a.day.localeCompare(b.day));

  // Per-type breakdown
  const byType = new Map<MailingType, MailingEvalRowFlagged[]>();
  for (const r of rows) {
    const arr = byType.get(r.mailing_type) ?? [];
    arr.push(r);
    byType.set(r.mailing_type, arr);
  }
  const typeBreakdown: MailingTypeBreakdown[] = Array.from(byType.entries()).map(([type, list]) => {
    const revRows = list.filter((r) => r.revenue != null);
    const totalRev = sumIf(revRows, (r) => r.revenue);
    const recipForRev = sumIf(revRows, (r) => r.recipients);
    return {
      type,
      sends: list.length,
      recipients: sumIf(
        list.filter((r) => r.recipients != null),
        (r) => r.recipients,
      ),
      opens: sumIf(
        list.filter((r) => r.unique_opens != null),
        (r) => r.unique_opens,
      ),
      clicks: sumIf(
        list.filter((r) => r.unique_clicks != null),
        (r) => r.unique_clicks,
      ),
      leads: sumIf(
        list.filter((r) => r.leads != null),
        (r) => r.leads,
      ),
      revenue: Math.round(totalRev * 100) / 100,
      etkp: recipForRev > 0 ? Math.round(((totalRev * 1000) / recipForRev) * 100) / 100 : null,
    };
  });

  // Overall summary — weighted aggregation (mirrors backend HistoryKpiCards)
  const opensRows = rows.filter((r) => r.unique_opens != null);
  const clicksRows = rows.filter((r) => r.unique_clicks != null);
  const leadsRows = rows.filter((r) => r.leads != null);
  const revRows = rows.filter((r) => r.revenue != null);
  const totalOpens = sumIf(opensRows, (r) => r.unique_opens);
  const opensDenom = sumIf(opensRows, (r) => r.recipients);
  const totalClicks = sumIf(clicksRows, (r) => r.unique_clicks);
  const clicksDenom = sumIf(clicksRows, (r) => r.recipients);
  const totalLeads = sumIf(leadsRows, (r) => r.leads);
  const leadsDenom = sumIf(leadsRows, (r) => r.recipients);
  const totalRevenue = sumIf(revRows, (r) => r.revenue);
  const revRecipients = sumIf(revRows, (r) => r.recipients);

  const summary: MailingEvalSummary = {
    windowDays: 0, // not recomputed client-side — unused by the UI
    totalSends: rows.length,
    totalRecipients: sumIf(
      rows.filter((r) => r.recipients != null),
      (r) => r.recipients,
    ),
    totalOpens,
    totalClicks,
    totalLeads,
    totalRevenue: Math.round(totalRevenue * 100) / 100,
    openRate: opensDenom > 0 ? totalOpens / opensDenom : null,
    clickRate: clicksDenom > 0 ? totalClicks / clicksDenom : null,
    leadRate: leadsDenom > 0 ? totalLeads / leadsDenom : null,
    revenuePerLead: totalLeads > 0 ? Math.round((totalRevenue / totalLeads) * 100) / 100 : null,
    etkp:
      revRecipients > 0 ? Math.round(((totalRevenue * 1000) / revRecipients) * 100) / 100 : null,
  };

  return {
    summary,
    typeBreakdown,
    dailyAggregates,
    rows,
    scannerCount: rows.filter((r) => r.scannerSuspect).length,
  };
}

/** The 5 SAM-eval KPI cards (Sendungen / Recipients / Leads / Revenue / ETKP).
 * Extracted so the Beide-Modus can stack one row per client. Uses only literal
 * colors + the shared render helpers — no theme dependency. */
function renderMailingKpiCards(summary: MailingEvalSummary, typeCount: number): JSX.Element {
  return (
    <Grid container spacing={1.5} mb={2}>
      <Grid item xs={6} md={2.4}>
        <Paper sx={{ p: 1.5, backgroundColor: '#ffffff' }}>
          <Typography variant="caption" sx={{ color: '#555' }}>
            Sendungen
          </Typography>
          <Typography variant="h5" sx={{ color: 'black' }}>
            {renderInt(summary.totalSends)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#555' }}>
            {typeCount} Typ(en) · gemischt
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} md={2.4}>
        <Paper sx={{ p: 1.5, backgroundColor: '#ffffff' }}>
          <Typography variant="caption" sx={{ color: '#555' }}>
            Recipients
          </Typography>
          <Typography variant="h5" sx={{ color: 'black' }}>
            {renderInt(summary.totalRecipients)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#555' }}>
            OR {renderRate(summary.totalOpens, summary.totalRecipients)}
            {' · '}
            CTR {renderRate(summary.totalClicks, summary.totalRecipients)}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} md={2.4}>
        <Paper sx={{ p: 1.5, backgroundColor: '#ffffff' }}>
          <Typography variant="caption" sx={{ color: '#555' }}>
            Leads
          </Typography>
          <Typography variant="h5" sx={{ color: '#2e7d32' }}>
            {renderInt(summary.totalLeads)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#555' }}>
            Lead-Rate {summary.leadRate != null ? `${(summary.leadRate * 100).toFixed(3)} %` : '–'}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} md={2.4}>
        <Paper sx={{ p: 1.5, backgroundColor: '#ffffff' }}>
          <Typography variant="caption" sx={{ color: '#555' }}>
            Revenue
          </Typography>
          <Typography variant="h5" sx={{ color: '#1976d2' }}>
            {renderEur(summary.totalRevenue)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#555' }}>
            je Lead {summary.revenuePerLead != null ? renderEur(summary.revenuePerLead) : '–'}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} md={2.4}>
        <Paper sx={{ p: 1.5, backgroundColor: '#ffffff' }}>
          <Typography variant="caption" sx={{ color: '#555' }}>
            ETKP
          </Typography>
          <Typography variant="h5" sx={{ color: '#1976d2' }}>
            {summary.etkp != null ? renderEur(summary.etkp) : '–'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#555' }}>
            Revenue × 1000 / Recipients
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
}

const DnvMigrationDashboard: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [summary, setSummary] = useState<CohortSummary[]>([]);
  const [listGrowth, setListGrowth] = useState<ListGrowthPoint[]>([]);
  const [inaktivState, setInaktivState] = useState<InaktivState | null>(null);
  const [failedCount, setFailedCount] = useState<number>(0);
  const [tiers, setTiers] = useState<TierMap | null>(null);
  const { filterState } = useContext(FilterContext)!;
  const samEvalRange = useMemo(() => {
    const fmt = (d: Date): string => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    if (filterState.startDate && filterState.endDate) {
      return { startDate: fmt(filterState.startDate), endDate: fmt(filterState.endDate) };
    }
    // Default: last 30 days when the FilterBar dates haven't been set.
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - 30);
    return { startDate: fmt(start), endDate: fmt(today) };
  }, [filterState.startDate, filterState.endDate]);
  const [samEval, setSamEval] = useState<MailingEvalData | null>(null);
  const [samEvalLoading, setSamEvalLoading] = useState<boolean>(true);
  const [samEvalError, setSamEvalError] = useState<string | null>(null);
  // Reaktivierungs-Sendungen (kalte Dormant-Audience) sind standardmäßig
  // ausgeblendet — ihre kalten Raten verzerren den SAM-Schnitt. Toggle blendet
  // sie in KPIs / Charts / Tabelle wieder ein.
  const [showReactivation, setShowReactivation] = useState(false);
  // Scanner-Klick-Ausreißer (Security-Appliance-Bots) aus der Tages-Klickrate
  // glätten. Standardmäßig an.
  const [smoothScanners, setSmoothScanners] = useState(true);
  // Client-Filter für die SAM-Auswertung: DNV, KME (Shadow-Sends) oder Beide.
  // Steuert den Fetch (?client=) + die Darstellung (Beide → per-Client-KPI-Split
  // + Client-Spalte).
  const [evalClient, setEvalClient] = useState<EvalClientFilter>('dnv');
  const derivedEval = useMemo(() => {
    const src = samEval;
    return src ? deriveMailingEval(src.rows, showReactivation) : null;
  }, [samEval, showReactivation]);
  // Beide-Modus: zusätzlich einen KPI-Split pro Client (gleiche Mathematik wie
  // deriveMailingEval, nur je Client-Teilmenge). Null außerhalb von 'both'.
  const perClientEval = useMemo(() => {
    if (!samEval || evalClient !== 'both') return null;
    return (['dnv', 'kme'] as EvalClient[]).map((c) => ({
      client: c,
      derived: deriveMailingEval(
        samEval.rows.filter((r) => r.client === c),
        showReactivation,
      ),
    }));
  }, [samEval, evalClient, showReactivation]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async (signal?: { cancelled: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/dnv-migration/stats');
      if (signal?.cancelled) return;
      const data = res.data?.data ?? {};
      setSummary(data.summary ?? []);
      setListGrowth(data.listGrowth ?? []);
      setInaktivState(data.inaktivState ?? null);
      setFailedCount(Number(data.failedCount) || 0);
      setTiers((data.tiers as TierMap | undefined) ?? null);
    } catch (e: any) {
      if (!signal?.cancelled) {
        setError(e?.response?.data?.error || e?.message || 'Fehler beim Laden');
      }
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };
    load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load]);

  // SAM-Evaluation fetcher — separate from the main /stats call because it has
  // its own window-day control and is fetched with the more expensive
  // verlag_affiliates JOIN. Re-fires on window change.
  useEffect(() => {
    let cancelled = false;
    setSamEvalLoading(true);
    setSamEvalError(null);
    api
      .get(
        `/api/dnv-migration/mailing-evaluation?startDate=${samEvalRange.startDate}&endDate=${samEvalRange.endDate}&client=${evalClient}`,
      )
      .then((res) => {
        if (cancelled) return;
        setSamEval(res.data?.data ?? null);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setSamEvalError(
          e?.response?.data?.error || e?.message || 'Fehler beim Laden der SAM-Evaluation',
        );
      })
      .finally(() => {
        if (!cancelled) setSamEvalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [samEvalRange.startDate, samEvalRange.endDate, evalClient]);

  const retryFailed = useCallback(async () => {
    setRetrying(true);
    try {
      const res = await api.post('/api/dnv-migration/retry-failed', { limit: 500 });
      const r = res.data?.data;
      setToast(
        `Erneut versucht: ${r.retried} — ${r.succeeded} erfolgreich, ${r.failed} fehlgeschlagen`,
      );
      await load();
    } catch (e: any) {
      setToast(`Retry fehlgeschlagen: ${e?.response?.data?.error || e?.message}`);
    } finally {
      setRetrying(false);
    }
  }, [load]);

  const cohortRow = useCallback(
    (cohort: Cohort): CohortSummary | undefined => summary.find((s) => String(s.cohort) === cohort),
    [summary],
  );

  const renderCohortCard = (cohort: Cohort) => {
    const row = cohortRow(cohort);
    const total = Number(row?.total ?? 0);
    const target = COHORT_TARGET[cohort];
    const backfillPct = target > 0 ? Math.min(100, (total / target) * 100) : 0;
    const mcSynced = Number(row?.mc_synced ?? 0);
    const mcFailed = Number(row?.mc_failed ?? 0);
    const itSynced = Number(row?.iterable_synced ?? 0);
    const itPending = Number(row?.iterable_pending ?? 0);

    return (
      <Paper sx={{ p: 2, backgroundColor: '#ffffff', height: '100%' }}>
        <Typography variant="h6" sx={{ color: 'black', mb: 0.5 }}>
          Cohort {COHORT_LABEL[cohort]}
        </Typography>
        <Box component="ul" sx={{ pl: 2.25, m: 0, mb: 1, color: '#555' }}>
          {COHORT_BLURB[cohort].map((line) => (
            <Typography
              key={line}
              component="li"
              variant="caption"
              sx={{ color: '#555', display: 'list-item', lineHeight: 1.4 }}
            >
              {line}
            </Typography>
          ))}
        </Box>
        <Typography variant="h3" sx={{ color: colors.greenAccent[500] }}>
          {fmt(total)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'black' }}>
          von ~{fmt(target)} Initial-Schätzung ({backfillPct.toFixed(1)} %)
        </Typography>
        <Box mt={2}>
          <Typography variant="body2" sx={{ color: 'black' }}>
            DNV Mailchimp: {fmt(mcSynced)} synced, {fmt(mcFailed)} failed
          </Typography>
          <Typography variant="body2" sx={{ color: 'black' }}>
            Iterable: {fmt(itSynced)} synced, {fmt(itPending)} pending
          </Typography>
          <Typography variant="body2" sx={{ color: 'black', mt: 1 }}>
            Quellen: rolling {fmt(row?.source_rolling)} · backfill {fmt(row?.source_backfill)} ·
            initial {fmt(row?.source_initial)}
          </Typography>
          {row?.last_added_at && (
            <Typography variant="caption" sx={{ color: 'black' }}>
              Letzter Eintrag: {new Date(row.last_added_at).toLocaleString('de-DE')}
            </Typography>
          )}
        </Box>
      </Paper>
    );
  };

  // Build chart series from listGrowth. Series order matters for `point`-scale
  // x-axis enumeration: the longest series (cumulative migrations, 16+ days)
  // MUST appear before the sparse series (DNV list history, ≤3 days at launch)
  // so the x-axis preserves chronological order.
  // Series grouped into two semantic blocks (mirrored in the legend by prefix):
  //   ① "Iterable → DNV"    — kumulative Migration aus Iterable (cohort A/B)
  //   ② "DNV-Liste heute"   — Mailchimp-Snapshot aus mc_list_growth_history
  //                            (gesamt + aktiv + inaktiv-suppressed)
  // Longest series first so the point-scale x-axis enumeration stays
  // chronological (16+ days vs ≤3 day list-history).
  const chartSeries = useMemo(() => {
    if (listGrowth.length === 0) return [];
    const labelDay = (s: string) => s.slice(5).replace('-', '.'); // YYYY-MM-DD → MM.DD
    const migInactive = listGrowth.map((p) => ({
      x: labelDay(p.day),
      y: Number(p.migrated_inactive_cum) || 0,
    }));
    const migEngaged = listGrowth.map((p) => ({
      x: labelDay(p.day),
      y: Number(p.migrated_engaged_cum) || 0,
    }));
    const subscribed = listGrowth
      .filter((p) => p.dnv_subscribed != null)
      .map((p) => ({ x: labelDay(p.day), y: p.dnv_subscribed as number }));
    const activeOnDnv = listGrowth
      .filter((p) => p.dnv_active != null)
      .map((p) => ({ x: labelDay(p.day), y: p.dnv_active as number }));
    const inactive = listGrowth
      .filter((p) => p.dnv_inactive != null)
      .map((p) => ({ x: labelDay(p.day), y: p.dnv_inactive as number }));
    return [
      // ① Iterable → DNV (cumulative migration)
      {
        id: '① Iterable → DNV: Cohort A (Inactive)',
        color: '#7b1fa2',
        data: migInactive,
      },
      {
        id: '① Iterable → DNV: Cohort B (Engaged Non-Conv.)',
        color: '#9575cd',
        data: migEngaged,
      },
      // ② DNV-Liste heute (Mailchimp snapshot)
      {
        id: '② DNV-Liste gesamt (subscribed)',
        color: '#1976d2',
        data: subscribed,
      },
      {
        id: '② DNV: aktiv (regulärer Versand)',
        color: '#2e7d32',
        data: activeOnDnv,
      },
      {
        id: '② DNV: Inaktiv-Tag (suppressed)',
        color: '#c62828',
        data: inactive,
      },
    ];
  }, [listGrowth]);

  const chartXTickValues = useMemo(() => {
    if (listGrowth.length === 0) return undefined;
    const labels = listGrowth.map((p) => p.day.slice(5).replace('-', '.'));
    // Show every ~7th label to avoid overlap
    const step = Math.max(1, Math.ceil(labels.length / 12));
    return labels.filter((_, i) => i % step === 0 || i === labels.length - 1);
  }, [listGrowth]);

  return (
    <Box m="20px">
      <Header
        title="DNV Migration & Inaktiv-System"
        subtitle="DNV-Empfänger, Inaktiv-System und Versand-Auswertung im Überblick"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ── Kohorten-Übersicht ───────────────────────────────────────── */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderCohortCard('inactive')}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderCohortCard('engaged_non_converter')}
            </Grid>
          </Grid>

          {/* ── Inaktiv-System Erklärung ─────────────────────────────────── */}
          <Box mt={4}>
            <Typography variant="h5" sx={{ color: colors.grey[100], mb: 2, fontWeight: 600 }}>
              Wie funktioniert das Inaktiv-System?
            </Typography>
            <Grid container spacing={2}>
              {/* Card 1: Warum */}
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: '#ffffff',
                    height: '100%',
                    borderLeft: '4px solid #1976d2',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <ShieldOutlined sx={{ color: '#1976d2' }} />
                    <Typography variant="subtitle1" sx={{ color: 'black', fontWeight: 600 }}>
                      Warum?
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                    {[
                      <>
                        Schützt die <strong>Sender-Reputation</strong> — zu viele Mails an stille
                        Empfänger schaden allen Sendungen
                      </>,
                      <>
                        Statt zu unsubscriben: erst <strong>als Inaktiv markieren</strong>, dann
                        gezielt versuchen zurückzuholen
                      </>,
                      <>
                        Nach mehreren ignorierten Versuchen:{' '}
                        <strong>dauerhafter Versand-Stopp</strong>
                      </>,
                      <>Newsletter laufen davon unabhängig weiter</>,
                    ].map((line, i) => (
                      <Typography
                        key={i}
                        component="li"
                        variant="body2"
                        sx={{ color: 'black', mb: 0.5 }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              </Grid>

              {/* Card 2: Zwei-Cron-Flow */}
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: '#ffffff',
                    height: '100%',
                    borderLeft: '4px solid #7b1fa2',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <ScheduleOutlined sx={{ color: '#7b1fa2' }} />
                    <Typography variant="subtitle1" sx={{ color: 'black', fontWeight: 600 }}>
                      Wann jemand als Inaktiv markiert wird
                    </Typography>
                  </Box>
                  <Box mb={1.5}>
                    <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 600 }}>
                      Allgemein
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'black' }}>
                      Wer seit 90 Tagen keine Mail mehr geöffnet hat
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 600 }}>
                      DNV-spezifisch
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'black', mb: 0.5 }}>
                      Wer seit 90 Tagen nichts mehr auf DNV geklickt hat — plus:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                      <Typography component="li" variant="body2" sx={{ color: 'black', mb: 0.5 }}>
                        <strong>Cohort A</strong>: sofort, keine Karenz
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ color: 'black' }}>
                        <strong>Cohort B</strong>: erst nach 60 Tagen
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Card 3: Live-Status */}
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: '#ffffff',
                    height: '100%',
                    borderLeft: '4px solid #c62828',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <TrendingDownOutlined sx={{ color: '#c62828' }} />
                    <Typography variant="subtitle1" sx={{ color: 'black', fontWeight: 600 }}>
                      Aktueller Stand
                    </Typography>
                  </Box>
                  {inaktivState ? (
                    (() => {
                      const total = inaktivState.subscribed;
                      const active = inaktivState.active;
                      const inactive = inaktivState.inactive;
                      const activePct = total > 0 ? (active / total) * 100 : 0;
                      const inactivePct = total > 0 ? (inactive / total) * 100 : 0;
                      return (
                        <>
                          <Typography variant="h4" sx={{ color: 'black' }}>
                            {fmt(total)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'black' }}>
                            DNV Liste gesamt (subscribed,{' '}
                            {new Date(inaktivState.date).toLocaleDateString('de-DE')})
                            {inaktivState.subscribed_delta != null && (
                              <>
                                {' · '}
                                <span
                                  style={{
                                    color: inaktivState.subscribed_delta > 0 ? '#2e7d32' : '#555',
                                  }}
                                >
                                  {fmtSigned(inaktivState.subscribed_delta)} ggü. Vortag
                                </span>
                              </>
                            )}
                          </Typography>

                          {/* Active vs Inactive split — two-column */}
                          <Box display="flex" gap={1.5} mt={2}>
                            <Box flex={1}>
                              <Typography
                                variant="caption"
                                sx={{ color: '#2e7d32', fontWeight: 700 }}
                              >
                                ✓ AKTIV (Versand)
                              </Typography>
                              <Typography variant="h5" sx={{ color: '#2e7d32' }}>
                                {fmt(active)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: 'black', display: 'block' }}
                              >
                                {activePct.toFixed(1)} %
                              </Typography>
                              {inaktivState.active_delta != null && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: inaktivState.active_delta >= 0 ? '#2e7d32' : '#c62828',
                                    display: 'block',
                                  }}
                                >
                                  {fmtSigned(inaktivState.active_delta)} ggü. Vortag
                                </Typography>
                              )}
                            </Box>
                            <Box flex={1}>
                              <Typography
                                variant="caption"
                                sx={{ color: '#c62828', fontWeight: 700 }}
                              >
                                ⊘ INAKTIV (suppressed)
                              </Typography>
                              <Typography variant="h5" sx={{ color: '#c62828' }}>
                                {fmt(inactive)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: 'black', display: 'block' }}
                              >
                                {inactivePct.toFixed(1)} %
                              </Typography>
                              {inaktivState.inactive_delta != null && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: inaktivState.inactive_delta > 0 ? '#c62828' : '#2e7d32',
                                    display: 'block',
                                  }}
                                >
                                  {fmtSigned(inaktivState.inactive_delta)} ggü. Vortag
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Stacked-bar visualization */}
                          <Box
                            sx={{
                              mt: 1.5,
                              height: 10,
                              borderRadius: 5,
                              overflow: 'hidden',
                              display: 'flex',
                              border: '1px solid #e0e0e0',
                            }}
                          >
                            <Box
                              sx={{ width: `${activePct}%`, backgroundColor: '#2e7d32' }}
                              title={`Aktiv: ${active.toLocaleString('de-DE')}`}
                            />
                            <Box
                              sx={{ width: `${inactivePct}%`, backgroundColor: '#c62828' }}
                              title={`Inaktiv: ${inactive.toLocaleString('de-DE')}`}
                            />
                          </Box>

                          {/* Footer: unsubscribed + cleaned context */}
                          <Typography
                            variant="caption"
                            sx={{ color: '#555', display: 'block', mt: 1.5 }}
                          >
                            Unsubscribed: {fmt(inaktivState.unsubscribed)} · Cleaned (Bounce):{' '}
                            {fmt(inaktivState.cleaned)}
                          </Typography>
                        </>
                      );
                    })()
                  ) : (
                    <Typography variant="body2" sx={{ color: 'black' }}>
                      Noch keine Snapshot-Daten — Cron `mailchimp_list_growth_cache` läuft täglich
                      02:30 Berlin.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {/* ── SAM-Versand-Regeln nach Inaktiv-Tier ────────────────────── */}
          <Box mt={4}>
            <Box display="flex" alignItems="baseline" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <TuneOutlined sx={{ color: colors.grey[100] }} />
                <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 600 }}>
                  Wie Inaktive zurückgeholt werden sollen
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: '#fff3e0',
                  border: '1px solid #ed6c02',
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" sx={{ color: '#bf360c', fontWeight: 600 }}>
                  Einteilung LIVE · Versand (Probe-Mails) AUSSTEHEND
                </Typography>
              </Box>
            </Box>
            {/* 4-step funnel showing the full lifecycle including the 90d gate */}
            {(() => {
              const taggedTotal =
                (tiers?.recent.total ?? 0) + (tiers?.stale.total ?? 0) + (tiers?.dark.total ?? 0);
              const outcomeTotal =
                (tiers?.recent.reactivated ?? 0) +
                (tiers?.stale.reactivated ?? 0) +
                (tiers?.dark.reactivated ?? 0) +
                (tiers?.recent.permanent_dark ?? 0) +
                (tiers?.stale.permanent_dark ?? 0) +
                (tiers?.dark.permanent_dark ?? 0);
              const stepBoxSx = {
                flex: 1,
                minWidth: 0,
                p: 1.5,
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
              } as const;
              const arrowSx = { color: '#777', mx: { xs: 0, md: 0.5 } } as const;
              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'stretch', md: 'stretch' },
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Box sx={{ ...stepBoxSx, borderTop: '3px solid #1976d2' }}>
                    <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 700 }}>
                      1 · Alle DNV-Empfänger
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'black', mt: 0.5 }}>
                      {fmt(inaktivState?.subscribed ?? null)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#555' }}>
                      Mailchimp Liste gesamt
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 0.5,
                    }}
                  >
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      <ArrowForwardOutlined sx={arrowSx} />
                    </Box>
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                      <ArrowDownwardOutlined sx={arrowSx} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: '#555', textAlign: 'center', maxWidth: 140 }}
                    >
                      90 Tage kein DNV-Klick
                      <br />+ Karenzregeln pro Cohort
                    </Typography>
                  </Box>
                  <Box sx={{ ...stepBoxSx, borderTop: '3px solid #c62828' }}>
                    <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 700 }}>
                      2 · Inaktiv getagged
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'black', mt: 0.5 }}>
                      {fmt(taggedTotal)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#555' }}>
                      Tag = Inaktiv auf DNV
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 0.5,
                    }}
                  >
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      <ArrowForwardOutlined sx={arrowSx} />
                    </Box>
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                      <ArrowDownwardOutlined sx={arrowSx} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: '#555', textAlign: 'center', maxWidth: 160 }}
                    >
                      Einteilung nach
                      <br />
                      letztem Engagement
                    </Typography>
                  </Box>
                  <Box sx={{ ...stepBoxSx, borderTop: '3px solid #7b1fa2' }}>
                    <Typography variant="caption" sx={{ color: '#7b1fa2', fontWeight: 700 }}>
                      3 · Tier-klassifiziert
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'black', mt: 0.5 }}>
                      {fmt(taggedTotal)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#555' }}>
                      recent / stale / dark (siehe Karten unten)
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 0.5,
                    }}
                  >
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      <ArrowForwardOutlined sx={{ ...arrowSx, color: '#ed6c02' }} />
                    </Box>
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                      <ArrowDownwardOutlined sx={{ ...arrowSx, color: '#ed6c02' }} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: '#ed6c02', textAlign: 'center', maxWidth: 140, fontWeight: 600 }}
                    >
                      Phase 2 ·<br />
                      tier-spezifische
                      <br />
                      Probe-Mails
                    </Typography>
                  </Box>
                  <Box sx={{ ...stepBoxSx, borderTop: '3px solid #ed6c02', opacity: 0.7 }}>
                    <Typography variant="caption" sx={{ color: '#ed6c02', fontWeight: 700 }}>
                      4 · Probe-Ergebnis
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'black', mt: 0.5 }}>
                      {fmt(outcomeTotal)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#555' }}>
                      reactivated · permanent_dark
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
            <Box
              component="ul"
              sx={{
                pl: 2.5,
                m: 0,
                mb: 2,
                color: colors.grey[200],
                '& > li': { mb: 0.3 },
              }}
            >
              <Typography
                component="li"
                variant="body2"
                sx={{ color: colors.grey[200], display: 'list-item' }}
              >
                <strong>Schritt 1→2:</strong> Wer wird überhaupt als Inaktiv markiert?
              </Typography>
              <Typography
                component="li"
                variant="body2"
                sx={{ color: colors.grey[200], display: 'list-item' }}
              >
                <strong>Schritt 2→3:</strong> Wie aktiv waren die Markierten zuletzt? → in drei
                Tiers sortiert
              </Typography>
              <Typography
                component="li"
                variant="body2"
                sx={{ color: colors.grey[200], display: 'list-item' }}
              >
                <strong>Schritt 3→4:</strong> Pro Tier eigene Probe-Mails (kommt in Phase 2)
              </Typography>
              <Typography
                component="li"
                variant="body2"
                sx={{ color: colors.grey[200], display: 'list-item' }}
              >
                <strong>Heute:</strong> alle Markierten gleich behandelt — kein SAM-Versand
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {(['recent', 'stale', 'dark'] as TierBucket[]).map((bucket) => {
                const rule = TIER_RULES[bucket];
                const stat = tiers?.[bucket];
                const total = stat?.total ?? 0;
                const probing = stat?.probing ?? 0;
                const reactivated = stat?.reactivated ?? 0;
                const permanentDark = stat?.permanent_dark ?? 0;
                const tierTotal =
                  (tiers?.recent.total ?? 0) + (tiers?.stale.total ?? 0) + (tiers?.dark.total ?? 0);
                const pct = tierTotal > 0 ? (total / tierTotal) * 100 : 0;
                return (
                  <Grid item xs={12} md={4} key={bucket}>
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: '#ffffff',
                        height: '100%',
                        borderTop: `4px solid ${rule.color}`,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="baseline"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Typography variant="subtitle1" sx={{ color: rule.color, fontWeight: 700 }}>
                          Reaktivierung-{rule.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}>
                          {pct.toFixed(1)} %
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: 'black', mb: 0.5 }}>
                        <strong>Kriterium:</strong> {rule.criterion}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'black', mb: 1.5 }}>
                        <strong>Kadenz:</strong> {rule.cadence}
                      </Typography>
                      <Typography variant="h4" sx={{ color: rule.color, mb: 0.5 }}>
                        {fmt(total)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#555' }}>
                        DNV-Empfänger in diesem Tier
                      </Typography>
                      <Box mt={1.5}>
                        <Typography variant="caption" sx={{ color: 'black', display: 'block' }}>
                          probing: {fmt(probing)} · reactivated: {fmt(reactivated)} ·
                          permanent_dark: {fmt(permanentDark)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* ── Listen-Wachstum Chart ────────────────────────────────────── */}
          <Paper sx={{ p: 2, mt: 4, backgroundColor: '#ffffff', height: 500 }}>
            <Typography variant="h6" sx={{ color: 'black', mb: 0.5 }}>
              Listen-Wachstum DNV (letzte 90 Tage)
            </Typography>
            <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 1 }}>
              <strong style={{ color: '#7b1fa2' }}>① Iterable → DNV</strong>: wieviel pro Kohorte
              schon migriert wurde · <strong style={{ color: '#1976d2' }}>② DNV-Liste heute</strong>
              : Aufteilung in aktiv (regulärer Versand) vs. Inaktiv-markiert (Snapshot-Daten ab
              23.06.)
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'baseline',
                mb: 1.5,
                p: 1,
                backgroundColor: '#fffaf2',
                border: '1px solid #f5d3a3',
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: '#bf360c', fontWeight: 700 }}>
                System-Timeline:
              </Typography>
              <Typography variant="caption" sx={{ color: '#333' }}>
                <strong>10.06.</strong> Iterable→DNV-Migration startet (1.700 Cohort A + 700 Cohort
                B pro Tag)
              </Typography>
              <Typography variant="caption" sx={{ color: '#999' }}>
                ·
              </Typography>
              <Typography variant="caption" sx={{ color: '#333' }}>
                <strong>23.06.</strong> Inaktiv-System dashboard-seitig aktiviert (manueller Test
                gegen alle 20 MC-Listen)
              </Typography>
              <Typography variant="caption" sx={{ color: '#999' }}>
                ·
              </Typography>
              <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 600 }}>
                <strong>24.06.</strong> Erste planmäßige 04:15-Cron-Ausführung → DNV Inaktiv-Pool 0
                → 39.028 (erklärt den Sprung im Chart)
              </Typography>
            </Box>
            {chartSeries.every((s) => s.data.length === 0) ? (
              <Typography variant="body2" sx={{ color: 'black' }}>
                Noch keine Daten.
              </Typography>
            ) : (
              <Box height={380}>
                <ResponsiveLine
                  data={chartSeries}
                  margin={{ top: 30, right: 280, bottom: 70, left: 70 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
                  curve="monotoneX"
                  colors={(d) => d.color as string}
                  markers={[
                    {
                      axis: 'x',
                      value: '06.24',
                      lineStyle: {
                        stroke: '#c62828',
                        strokeWidth: 1.5,
                        strokeDasharray: '4 4',
                      },
                      legend: '24.06. · Inaktiv-Cron erste Ausführung',
                      legendOrientation: 'horizontal',
                      legendPosition: 'top',
                      textStyle: { fill: '#c62828', fontSize: 10, fontWeight: 600 },
                    },
                  ]}
                  axisBottom={{
                    tickRotation: -45,
                    tickValues: chartXTickValues,
                    legend: 'Datum',
                    legendPosition: 'middle',
                    legendOffset: 55,
                  }}
                  axisLeft={{
                    legend: 'Empfänger',
                    legendPosition: 'middle',
                    legendOffset: -55,
                    format: (v) => Number(v).toLocaleString('de-DE'),
                  }}
                  enablePoints={true}
                  pointSize={4}
                  pointBorderWidth={1}
                  pointBorderColor={{ from: 'serieColor' }}
                  enableGridX={false}
                  useMesh={true}
                  enableSlices="x"
                  sliceTooltip={({ slice }) => (
                    <Box
                      sx={{
                        background: '#fff',
                        p: 1.5,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        boxShadow: 3,
                        minWidth: 260,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: '#333',
                          display: 'block',
                          mb: 0.75,
                          borderBottom: '1px solid #eee',
                          pb: 0.5,
                        }}
                      >
                        {String(slice.points[0]?.data.xFormatted ?? '')}
                      </Typography>
                      {slice.points.map((p) => (
                        <Box
                          key={p.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            mb: 0.25,
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              backgroundColor: p.serieColor,
                              borderRadius: '50%',
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: '#333', flex: 1, mr: 1, fontSize: '0.75rem' }}
                          >
                            {String(p.serieId)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, color: '#333', fontSize: '0.75rem' }}
                          >
                            {Number(p.data.y).toLocaleString('de-DE')}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  legends={[
                    {
                      anchor: 'right',
                      direction: 'column',
                      translateX: 270,
                      itemWidth: 260,
                      itemHeight: 24,
                      symbolSize: 12,
                      symbolShape: 'circle',
                      itemTextColor: '#333',
                    },
                  ]}
                  theme={{
                    axis: {
                      ticks: { text: { fill: '#333' } },
                      legend: { text: { fill: '#333' } },
                    },
                    legends: { text: { fill: '#333' } },
                    tooltip: { container: { color: 'black' } },
                  }}
                />
              </Box>
            )}
          </Paper>

          {/* ── SAM-Performance Evaluation ─────────────────────────────── */}
          <Box mt={4}>
            <Box display="flex" alignItems="baseline" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <CampaignOutlined sx={{ color: colors.grey[100] }} />
                <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 600 }}>
                  SAM-Performance (DNV / KME)
                </Typography>
                <Button
                  size="small"
                  component={RouterLink}
                  to="/adserver/sam-scheduling"
                  variant="outlined"
                  sx={{
                    ml: 1.5,
                    color: colors.grey[100],
                    borderColor: colors.grey[200],
                    textTransform: 'none',
                  }}
                  startIcon={<CampaignOutlined fontSize="small" />}
                >
                  Mehr SAM-Auswertungen
                </Button>
              </Box>
              <Typography variant="caption" sx={{ color: colors.grey[200] }}>
                Zeitraum: <strong>{samEvalRange.startDate}</strong> –{' '}
                <strong>{samEvalRange.endDate}</strong> · Datums-Filter oben anpassbar
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: colors.grey[200], mb: 2 }}>
              {evalClient === 'kme'
                ? 'KME Shadow-Sends (08:00-Re-Send der DNV-VNR-SAM an idle Tagen) mit Leads & Umsatz.'
                : evalClient === 'both'
                  ? 'DNV-Sendungen (SAM, Webinar, PLX, Asset-Analyse) + KME Shadow-Sends mit Leads & Umsatz — Client-Filter unten.'
                  : 'Alle DNV-Sendungen (SAM, Webinar, PLX, Asset-Analyse) mit Leads & Umsatz.'}{' '}
              Tiefere SAM-Auswertungen findest du auf{' '}
              <RouterLink
                to="/adserver/sam-scheduling"
                style={{ color: colors.greenAccent[400], textDecoration: 'underline' }}
              >
                /adserver/sam-scheduling
              </RouterLink>
              .
            </Typography>

            {/* Cadence-explanation card */}
            <Paper
              sx={{
                p: 1.5,
                mb: 2,
                backgroundColor: '#fffaf2',
                borderLeft: '4px solid #ed6c02',
              }}
            >
              <Box display="flex" alignItems="flex-start" gap={1.5}>
                <InfoOutlined sx={{ color: '#ed6c02', mt: 0.3 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'black', fontWeight: 700, mb: 0.5 }}>
                    Warum so wenige Sendungen pro Woche?
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'black' }}>
                    Aktuell nur <strong>3 SAMs pro Woche</strong> (Mo/Mi/Fr) — bewusst limitiert zum
                    Schutz der <strong>Absender-Reputation</strong>. Sobald sich die Reputation
                    durch bessere Öffnungsraten erholt, wird die Sende-Frequenz schrittweise erhöht.
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {samEvalError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSamEvalError(null)}>
                {samEvalError}
              </Alert>
            )}

            {samEvalLoading ? (
              <Box display="flex" justifyContent="center" my={3}>
                <CircularProgress size={28} />
              </Box>
            ) : derivedEval ? (
              <>
                {/* Toggles: client filter + reactivation visibility + scanner-click smoothing */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" sx={{ color: colors.grey[100], fontWeight: 600 }}>
                      Client:
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={evalClient}
                      onChange={(_e, v) => {
                        if (v) setEvalClient(v as EvalClientFilter);
                      }}
                      sx={{
                        '& .MuiToggleButton-root': {
                          textTransform: 'none',
                          px: 1.5,
                          py: 0.3,
                          color: colors.grey[100],
                        },
                        '& .Mui-selected': {
                          backgroundColor: '#1976d2 !important',
                          color: '#fff !important',
                        },
                      }}
                    >
                      <ToggleButton value="dnv">DNV</ToggleButton>
                      <ToggleButton value="kme">KME</ToggleButton>
                      <ToggleButton value="both">Beide</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={showReactivation}
                        onChange={(e) => setShowReactivation(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ color: colors.grey[100] }}>
                        Reaktivierungs-Sendungen einbeziehen
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={smoothScanners}
                        onChange={(e) => setSmoothScanners(e.target.checked)}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="caption" sx={{ color: colors.grey[100] }}>
                          Scanner-Klicks glätten
                        </Typography>
                        <Tooltip
                          title={`Sendungen mit unplausibler Klick-zu-Öffnungs-Rate (> ${Math.round(
                            SCANNER_CTOR_THRESHOLD * 100,
                          )} %, ≥ ${SCANNER_MIN_OPENS} Öffnungen) werden als Bot-/Security-Scanner-Klicks eingestuft und aus der Tages-Klickrate ausgenommen — ein einzelner Scanner-Sturm sprengt sonst die Achse. Öffnungsrate bleibt unberührt (Mailchimp filtert Bot-Öffnungen bereits via proxy_excluded).`}
                        >
                          <InfoOutlined sx={{ fontSize: 15, color: colors.grey[300] }} />
                        </Tooltip>
                      </Box>
                    }
                  />
                  {derivedEval.scannerCount > 0 && (
                    <Chip
                      size="small"
                      icon={<WarningAmberOutlined sx={{ fontSize: 15 }} />}
                      label={`${derivedEval.scannerCount} Scanner-verdächtige Sendung(en)`}
                      sx={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }}
                    />
                  )}
                </Box>

                {derivedEval.rows.length === 0 ? (
                  <Paper sx={{ p: 3, backgroundColor: '#ffffff' }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: 'black', fontWeight: 600, mb: 0.5 }}
                    >
                      Keine Sendungen im gewählten Zeitraum
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}>
                      {evalClient === 'kme'
                        ? 'KME sendet DNVs VNR-SAM als Shadow-Send (08:00 an versandfreien Tagen). Die Auswertung erscheint hier, sobald der erste Shadow-Send tatsächlich versendet & in den Kampagnen-Cache synchronisiert wurde — aktuell ist der jüngste Versand nur geplant (noch nicht versendet), ältere Versuche wurden abgebrochen.'
                        : evalClient === 'both'
                          ? 'Für DNV und KME liegen im gewählten Zeitraum keine Sendungen vor.'
                          : 'Für DNV liegen im gewählten Zeitraum keine Sendungen vor. Datumsbereich oben anpassen.'}
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    {/* KPI cards — Beide-Modus: eine beschriftete Zeile pro Client,
                    sonst eine kombinierte Zeile über die geladenen Rows. */}
                    {evalClient === 'both' && perClientEval ? (
                      <Box mb={1}>
                        {perClientEval.map((pc) => (
                          <Box key={pc.client} mb={1.5}>
                            <Chip
                              label={EVAL_CLIENT_LABEL[pc.client]}
                              size="small"
                              sx={{
                                backgroundColor: EVAL_CLIENT_COLOR[pc.client],
                                color: '#fff',
                                fontWeight: 700,
                                mb: 1,
                              }}
                            />
                            {renderMailingKpiCards(
                              pc.derived.summary,
                              pc.derived.typeBreakdown.length,
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      renderMailingKpiCards(derivedEval.summary, derivedEval.typeBreakdown.length)
                    )}

                    {/* Per-Type Breakdown */}
                    <Paper sx={{ p: 1.5, backgroundColor: '#ffffff', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: 'black', mb: 1 }}>
                        Aufschlüsselung nach Mailing-Typ
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {[
                                'Typ',
                                'Sendungen',
                                'Empfänger',
                                'Öffnungsrate',
                                'Klickrate',
                                'Leads',
                                'Revenue',
                                'ETKP',
                              ].map((h) => (
                                <TableCell
                                  key={h}
                                  sx={{
                                    backgroundColor: '#e0e0e0 !important',
                                    color: 'black',
                                    fontWeight: 'bold',
                                  }}
                                  align={['Typ'].includes(h) ? 'left' : 'right'}
                                >
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {derivedEval.typeBreakdown
                              .slice()
                              .sort((a, b) => b.recipients - a.recipients)
                              .map((row) => (
                                <TableRow key={row.type} hover>
                                  <TableCell sx={{ color: 'black' }}>
                                    <Chip
                                      label={MAILING_TYPE_LABEL[row.type]}
                                      size="small"
                                      sx={{
                                        backgroundColor: MAILING_TYPE_COLOR[row.type],
                                        color: '#fff',
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ color: 'black' }} align="right">
                                    {renderInt(row.sends)}
                                  </TableCell>
                                  <TableCell sx={{ color: 'black' }} align="right">
                                    {renderInt(row.recipients)}
                                  </TableCell>
                                  <TableCell sx={{ color: 'black' }} align="right">
                                    {renderRate(row.opens, row.recipients)}
                                  </TableCell>
                                  <TableCell sx={{ color: 'black' }} align="right">
                                    {renderRate(row.clicks, row.recipients)}
                                  </TableCell>
                                  <TableCell sx={{ color: 'black' }} align="right">
                                    {renderInt(row.leads)}
                                  </TableCell>
                                  <TableCell sx={{ color: 'black' }} align="right">
                                    {row.revenue > 0 ? renderEur(row.revenue) : '–'}
                                  </TableCell>
                                  <TableCell sx={{ color: 'black' }} align="right">
                                    {row.etkp != null ? renderEur(row.etkp) : '–'}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Typography
                        variant="caption"
                        sx={{ color: '#555', display: 'block', mt: 0.5 }}
                      >
                        SAM: Leads & Umsatz aus VNR-Zuordnung. Webinar: „Leads“ = Anmeldungen,
                        Umsatz = CPA-Wert der Bestellungen, die zu diesen Anmeldungen gehören. PLX &
                        Asset-Analyse laufen noch über andere Tracking-Wege.
                      </Typography>
                    </Paper>

                    {/* Per-Metric Timeline Charts (6 in a 3×2 grid) */}
                    <Grid container spacing={2} mb={2}>
                      {(
                        [
                          {
                            id: 'recipients',
                            label: 'Empfänger pro Tag',
                            color: '#1976d2',
                            extract: (d: MailingDaily) => d.recipients,
                            format: (v: number) => Number(v).toLocaleString('de-DE'),
                            tooltipFormat: (v: number) => Number(v).toLocaleString('de-DE'),
                            axisLabel: '',
                          },
                          {
                            id: 'openRate',
                            label: 'Öffnungsrate pro Tag (%)',
                            color: '#7b1fa2',
                            extract: (d: MailingDaily) =>
                              d.recipients > 0
                                ? Math.round((d.opens / d.recipients) * 100 * 100) / 100
                                : 0,
                            format: (v: number) => `${Number(v).toFixed(1)} %`,
                            tooltipFormat: (v: number) => `${Number(v).toFixed(2)} %`,
                            axisLabel: '%',
                          },
                          {
                            id: 'clickRate',
                            label: 'Klickrate pro Tag (%)',
                            color: '#ed6c02',
                            // Scanner guard: when smoothing is on, exclude scanner-suspect
                            // sends from BOTH numerator and denominator so the day shows the
                            // click rate of clean sends only (raw = one storm blows the axis).
                            extract: (d: MailingDailyGuarded) => {
                              const clicks = smoothScanners ? d.clicksClean : d.clicks;
                              const recip = smoothScanners ? d.recipientsClean : d.recipients;
                              return recip > 0 ? Math.round((clicks / recip) * 100 * 100) / 100 : 0;
                            },
                            // Days whose only send(s) were scanner-suspect leave no clean
                            // click signal — skip the point (gap) rather than plotting a
                            // misleading 0 %.
                            skip: (d: MailingDailyGuarded) =>
                              smoothScanners && d.scannerSends > 0 && d.recipientsClean === 0,
                            format: (v: number) => `${Number(v).toFixed(2)} %`,
                            tooltipFormat: (v: number) => `${Number(v).toFixed(3)} %`,
                            axisLabel: '%',
                          },
                          {
                            id: 'leads',
                            label: 'Leads pro Tag',
                            color: '#2e7d32',
                            extract: (d: MailingDaily) => d.leads,
                            format: (v: number) => Number(v).toLocaleString('de-DE'),
                            tooltipFormat: (v: number) => Number(v).toLocaleString('de-DE'),
                            axisLabel: '',
                          },
                          {
                            id: 'revenue',
                            label: 'Revenue pro Tag (€)',
                            color: '#1976d2',
                            extract: (d: MailingDaily) => Math.round(d.revenue * 100) / 100,
                            format: (v: number) => `${Number(v).toLocaleString('de-DE')} €`,
                            tooltipFormat: (v: number) =>
                              `${Number(v).toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })} €`,
                            axisLabel: '€',
                          },
                          {
                            id: 'etkp',
                            label: 'ETKP pro Tag (€)',
                            color: '#c62828',
                            extract: (d: MailingDaily) =>
                              d.recipients > 0
                                ? Math.round(((d.revenue * 1000) / d.recipients) * 100) / 100
                                : 0,
                            format: (v: number) =>
                              `${Number(v).toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })} €`,
                            tooltipFormat: (v: number) =>
                              `${Number(v).toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })} €`,
                            axisLabel: '€',
                          },
                        ] as Array<{
                          id: string;
                          label: string;
                          color: string;
                          extract: (d: MailingDailyGuarded) => number;
                          skip?: (d: MailingDailyGuarded) => boolean;
                          format: (v: number) => string;
                          tooltipFormat: (v: number) => string;
                          axisLabel: string;
                        }>
                      ).map((metric) => (
                        <Grid item xs={12} md={4} key={metric.id}>
                          <Paper sx={{ p: 1.5, backgroundColor: '#ffffff', height: 240 }}>
                            <Typography variant="subtitle2" sx={{ color: 'black', mb: 0.5 }}>
                              {metric.label}
                            </Typography>
                            {derivedEval.dailyAggregates.length === 0 ? (
                              <Typography variant="caption" sx={{ color: '#555' }}>
                                Keine Daten.
                              </Typography>
                            ) : (
                              <Box height={195}>
                                <ResponsiveLine
                                  data={[
                                    {
                                      id: metric.label,
                                      color: metric.color,
                                      data: derivedEval.dailyAggregates
                                        .filter((d) => !metric.skip?.(d))
                                        .map((d) => ({
                                          x: d.day.slice(5).replace('-', '.'),
                                          y: metric.extract(d),
                                        })),
                                    },
                                  ]}
                                  margin={{ top: 8, right: 18, bottom: 38, left: 50 }}
                                  xScale={{ type: 'point' }}
                                  yScale={{ type: 'linear', min: 0, max: 'auto' }}
                                  colors={(d) => d.color as string}
                                  enablePoints
                                  pointSize={5}
                                  pointBorderWidth={1}
                                  pointBorderColor={{ from: 'serieColor' }}
                                  axisBottom={{ tickRotation: -40, tickPadding: 4 }}
                                  axisLeft={{ format: metric.format }}
                                  enableGridX={false}
                                  useMesh
                                  enableSlices="x"
                                  sliceTooltip={({ slice }) => (
                                    <Box
                                      sx={{
                                        background: '#fff',
                                        p: 1,
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        boxShadow: 3,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 700,
                                          color: '#333',
                                          display: 'block',
                                          mb: 0.25,
                                        }}
                                      >
                                        {String(slice.points[0]?.data.xFormatted ?? '')}
                                      </Typography>
                                      {slice.points.map((p) => (
                                        <Box
                                          key={p.id}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 8,
                                              height: 8,
                                              backgroundColor: p.serieColor,
                                              borderRadius: '50%',
                                            }}
                                          />
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 700,
                                              color: '#333',
                                              fontSize: '0.72rem',
                                            }}
                                          >
                                            {metric.tooltipFormat(Number(p.data.y))}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                  theme={{
                                    axis: {
                                      ticks: { text: { fill: '#333', fontSize: 10 } },
                                      legend: { text: { fill: '#333' } },
                                    },
                                  }}
                                />
                              </Box>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Per-send table */}
                    <Paper sx={{ p: 1.5, backgroundColor: '#ffffff' }}>
                      <Typography variant="subtitle2" sx={{ color: 'black', mb: 1 }}>
                        Einzelne Sendungen
                      </Typography>
                      {derivedEval.rows.length === 0 ? (
                        <Typography variant="caption" sx={{ color: '#555' }}>
                          Keine Sendungen im Zeitraum.
                        </Typography>
                      ) : (
                        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                {[
                                  'Datum',
                                  ...(evalClient === 'both' ? ['Client'] : []),
                                  'Typ',
                                  'Subject / Title',
                                  'Produkt',
                                  'Recipients',
                                  'OR',
                                  'CTR',
                                  'Leads',
                                  'Revenue',
                                  'ETKP',
                                ].map((h) => (
                                  <TableCell
                                    key={h}
                                    sx={{
                                      backgroundColor: '#e0e0e0 !important',
                                      color: 'black',
                                      fontWeight: 'bold',
                                    }}
                                    align={
                                      [
                                        'Recipients',
                                        'OR',
                                        'CTR',
                                        'Leads',
                                        'Revenue',
                                        'ETKP',
                                      ].includes(h)
                                        ? 'right'
                                        : 'left'
                                    }
                                  >
                                    {h}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {derivedEval.rows.slice(0, 30).map((r) => {
                                const displaySubject = r.subject ?? r.sam_subject ?? r.title ?? '–';
                                return (
                                  <TableRow key={r.mc_campaign_id} hover>
                                    <TableCell sx={{ color: 'black', whiteSpace: 'nowrap' }}>
                                      {formatBerlin(r.send_date)}
                                    </TableCell>
                                    {evalClient === 'both' && (
                                      <TableCell>
                                        <Chip
                                          label={EVAL_CLIENT_LABEL[r.client]}
                                          size="small"
                                          sx={{
                                            backgroundColor: EVAL_CLIENT_COLOR[r.client],
                                            color: '#fff',
                                            fontWeight: 600,
                                          }}
                                        />
                                      </TableCell>
                                    )}
                                    <TableCell>
                                      <Chip
                                        label={MAILING_TYPE_LABEL[r.mailing_type]}
                                        size="small"
                                        sx={{
                                          backgroundColor: MAILING_TYPE_COLOR[r.mailing_type],
                                          color: '#fff',
                                          fontWeight: 600,
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        color: 'black',
                                        maxWidth: 280,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={displaySubject}
                                    >
                                      {displaySubject}
                                    </TableCell>
                                    <TableCell sx={{ color: 'black' }}>
                                      {r.product_name ?? '–'}
                                    </TableCell>
                                    <TableCell sx={{ color: 'black' }} align="right">
                                      {renderInt(r.recipients)}
                                    </TableCell>
                                    <TableCell sx={{ color: 'black' }} align="right">
                                      {renderRate(r.unique_opens, r.recipients)}
                                    </TableCell>
                                    <TableCell
                                      sx={{ color: r.scannerSuspect ? '#e65100' : 'black' }}
                                      align="right"
                                    >
                                      <Box
                                        display="inline-flex"
                                        alignItems="center"
                                        justifyContent="flex-end"
                                        gap={0.5}
                                      >
                                        {r.scannerSuspect && (
                                          <Tooltip
                                            title={`Verdacht auf Scanner-/Bot-Klicks: ${r.unique_clicks?.toLocaleString(
                                              'de-DE',
                                            )} Klicks bei ${r.unique_opens?.toLocaleString(
                                              'de-DE',
                                            )} Öffnungen (CTOR ${
                                              r.unique_opens
                                                ? Math.round(
                                                    ((r.unique_clicks ?? 0) / r.unique_opens) * 100,
                                                  )
                                                : 0
                                            } %). Aus der geglätteten Tages-Klickrate ausgenommen.`}
                                          >
                                            <WarningAmberOutlined sx={{ fontSize: 15 }} />
                                          </Tooltip>
                                        )}
                                        {renderRate(r.unique_clicks, r.recipients)}
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ color: 'black' }} align="right">
                                      {renderInt(r.leads)}
                                    </TableCell>
                                    <TableCell sx={{ color: 'black' }} align="right">
                                      {renderEur(r.revenue)}
                                    </TableCell>
                                    <TableCell sx={{ color: 'black' }} align="right">
                                      {renderEtkp(r.revenue, r.recipients)}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Paper>
                  </>
                )}
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'black' }}>
                Keine Daten.
              </Typography>
            )}
          </Box>

          {/* ── Retry-Footer (statt Top-Fehler-Tabelle) ─────────────────── */}
          <Paper sx={{ p: 2, mt: 3, backgroundColor: '#ffffff' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body1" sx={{ color: 'black' }}>
                  <strong>{fmt(failedCount)}</strong> Migration(en) mit Fehler
                </Typography>
                <Typography variant="caption" sx={{ color: '#555' }}>
                  Re-Push gegen DNV-Mailchimp (max. 500 pro Klick). Erfolgreiche Re-Pushes flippen
                  mc_status auf <code>synced</code>.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<RefreshOutlined />}
                onClick={retryFailed}
                disabled={retrying || failedCount === 0}
              >
                {retrying ? 'Läuft…' : 'Failed Rows erneut versuchen'}
              </Button>
            </Box>
          </Paper>
        </>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </Box>
  );
};

export default DnvMigrationDashboard;
